
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Settings, 
  PlayCircle, 
  BarChart3, 
  ShieldCheck, 
  ChevronRight,
  Zap,
  Lock,
  Eye,
  FileCheck,
  ArrowRight
} from 'lucide-react';
import { SERVICES, ETHICS } from '../constants';

const CORE_STAGES = [
  {
    title: "Fiscal Due Diligence",
    icon: Search,
    desc: "A forensic analysis of business transaction cycles, jurisdictional risks, and legacy data integrity.",
    color: "bg-blue-600"
  },
  {
    title: "ICFR Deployment",
    icon: Settings,
    desc: "Deploying a hardened control framework and integrating cloud-native compliance stacks for assurance.",
    color: "bg-indigo-600"
  },
  {
    title: "Stewardship",
    icon: PlayCircle,
    desc: "Continuous fiduciary processing, periodic reconciliation, and proactive statutory monitoring.",
    color: "bg-violet-600"
  },
  {
    title: "Reporting Hub",
    icon: BarChart3,
    desc: "Transforming raw records into IFRS-aligned board reporting and high-resolution MIS packages.",
    color: "bg-purple-600"
  }
];

const Process: React.FC = () => {
  const [activeTab, setActiveTab] = useState(SERVICES[0].id);
  const activeService = SERVICES.find(s => s.id === activeTab);

  return (
    <section id="process" className="py-32 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-widest mb-6 shadow-sm"
          >
            Engagement Lifecycle
          </motion.div>
          <h2 className="font-display font-bold text-4xl md:text-6xl text-slate-900 mb-6 tracking-tight">Systematic Fiduciary Ops</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg leading-relaxed">
            We architect end-to-end statutory ecosystems. Our methodology is built for institutional transparency and absolute regulatory resilience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
          {CORE_STAGES.map((stage, idx) => (
            <motion.div
              key={stage.title}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative group p-8 rounded-[2.5rem] bg-white border border-slate-200 hover:border-indigo-200 hover:shadow-2xl transition-all"
            >
              <div className={`w-14 h-14 rounded-2xl ${stage.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                <stage.icon size={28} />
              </div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-3 tracking-tight">{stage.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed font-medium">{stage.desc}</p>
              
              {idx < CORE_STAGES.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 translate-x-1/2 -translate-y-1/2 text-slate-300 group-hover:text-indigo-200 transition-colors">
                  <ChevronRight size={32} />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-1/3 space-y-8">
            <div>
              <h3 className="text-2xl font-display font-bold text-slate-900 mb-6 flex items-center gap-3 tracking-tight">
                <ShieldCheck className="text-indigo-600" />
                Service Nuances
              </h3>
              <div className="space-y-3">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveTab(s.id)}
                    className={`w-full text-left p-6 rounded-2xl transition-all relative overflow-hidden group border ${
                      activeTab === s.id
                        ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20 border-transparent'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600'
                    }`}
                  >
                    <div className="relative z-10 flex items-center justify-between">
                      <span className="font-bold text-lg tracking-tight">{s.title}</span>
                      {activeTab === s.id && <Zap size={18} className="animate-pulse" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-8 rounded-[2rem] bg-white border border-slate-200 shadow-lg">
              <h4 className="text-slate-400 font-bold mb-6 flex items-center gap-2 text-sm uppercase tracking-widest">
                <Lock size={16} className="text-indigo-600" />
                Strategic Ethos
              </h4>
              <div className="space-y-6">
                {ETHICS.map((ethic, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" />
                    <div>
                      <p className="text-slate-900 text-sm font-bold mb-0.5 tracking-tight">{ethic.title}</p>
                      <p className="text-slate-500 text-xs leading-relaxed font-medium">{ethic.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:w-2/3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white border border-slate-200 rounded-[3rem] p-8 md:p-14 shadow-2xl relative group overflow-hidden"
              >
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-50 blur-[100px] rounded-full group-hover:bg-indigo-100 transition-colors" />
                
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div>
                      <h3 className="text-3xl md:text-4xl font-display font-bold text-slate-900 mb-2 tracking-tight">{activeService?.title}</h3>
                      <p className="text-indigo-600 font-bold uppercase tracking-widest text-[10px] flex items-center gap-2">
                        <Eye size={14} /> Fiduciary Operational Methodology
                      </p>
                    </div>
                    <div className="px-6 py-3 rounded-2xl bg-slate-50 border border-slate-200">
                      <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest block mb-1">Audit Tier</span>
                      <span className="text-slate-900 text-sm font-bold flex items-center gap-2">
                        <FileCheck size={16} className="text-emerald-500" /> Institutional-Grade
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-10 relative">
                      <div className="absolute left-[11px] top-6 bottom-6 w-0.5 bg-slate-200" />
                      {activeService?.methodology.map((step, i) => (
                        <div key={i} className="relative pl-10 group/step">
                          <div className="absolute left-0 top-0 w-[24px] h-[24px] rounded-full bg-white border-2 border-indigo-200 flex items-center justify-center group-hover/step:border-indigo-600 group-hover/step:bg-indigo-600 transition-all z-10 shadow-sm">
                            <div className="w-1 h-1 rounded-full bg-indigo-600 group-hover/step:bg-white" />
                          </div>
                          <h5 className="text-slate-900 font-bold mb-1 tracking-tight group-hover/step:text-indigo-600 transition-colors">{step.title}</h5>
                          <p className="text-slate-500 text-sm leading-relaxed font-medium">{step.description}</p>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-8">
                      <div className="p-8 rounded-3xl bg-indigo-50 border border-indigo-100 group-hover:shadow-lg group-hover:shadow-indigo-600/5 transition-all">
                        <h4 className="text-indigo-600 font-bold uppercase tracking-widest text-[10px] mb-4">Strategic Focus</h4>
                        <p className="text-slate-900 text-xl font-display font-bold italic leading-relaxed tracking-tight">
                          "{activeService?.focus}"
                        </p>
                      </div>

                      <div className="space-y-4">
                        <h4 className="text-slate-400 font-bold uppercase tracking-widest text-[10px] ml-1">Key Deliverables</h4>
                        <div className="grid grid-cols-1 gap-3">
                          {activeService?.scope.slice(0, 4).map((item, i) => (
                            <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold hover:bg-white hover:shadow-sm transition-all">
                              <ArrowRight size={14} className="text-indigo-600" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
